import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Grid,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { GlobalStyles } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import PrimaryButton from "../components/PrimaryButton";
import axios from "../api/axios";

const CashierLogin = () => {
    const navigate = useNavigate();
    const [cashierData, setCashierData] = useState({
        username: "",
        password: "",
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const location = useLocation();
    const message = location?.state?.message;

    useEffect(() => {
        if (message === true) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [navigate, location]);

    const handlePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCashierData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validateForm = () => {
        let formErrors = {};
        if (!cashierData.username.trim()) {
            formErrors.username = "Username is required";
        }
        if (!cashierData.password.trim()) {
            formErrors.password = "Password is required";
        }
        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleLogin = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const response = await axios.post(
                "auth/user/kiosk-outlet-user-signin",
                {
                    username: cashierData.username,
                    password: cashierData.password,
                }
            );
            if (response.status === 200) {
                const expiryTime = Date.now() + 20 * 60 * 60 * 1000;
                localStorage.setItem("tokenExpiry", expiryTime.toString());
                localStorage.setItem("authToken", response?.data?.data?.jwt);
                localStorage.setItem(
                    "outlet",
                    response?.data?.data?.user?.outlet?._id
                );
                localStorage.setItem(
                    "paymentType",
                    response?.data?.data?.user?.outlet?.paymentType
                );
                const isEventOutlet =
                    response?.data?.data?.user?.outlet?.isEventOutlet;
                localStorage.setItem(
                    "isEventOutlet",
                    JSON.stringify(isEventOutlet)
                );
                const pineLabData =
                    response?.data?.data?.user?.outlet?.pineLabCredentials;
                localStorage.setItem(
                    "pineLabCredentials",
                    JSON.stringify(pineLabData)
                );

                const successMessage = response?.data?.data?.message;
                toast.success(successMessage, {
                    position: "bottom-center",
                    autoClose: 2000,
                    theme: "colored",
                });
                navigate("/explore");
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "Something went wrong. Please try again.";
                const status = error.response?.status;

                if (status === 406) {
                    errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.error;
                } else if (status === 412) {
                    errorMessage = error?.response?.data?.data?.message;
                } else if (status === 500) {
                    errorMessage = error?.response?.data?.error;
                } else if (status === 401 || status === 403) {
                    errorMessage = "Unauthorized. Please check your credentials.";
                }

                toast.error(errorMessage, {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });
            } else {
                toast.error("Network error. Please check your connection.", {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Keyframe animations injected globally */}
            <GlobalStyles
                styles={`
                    @keyframes slideInLeft {
                        from { transform: translateX(-130%); opacity: 0; }
                        to   { transform: translateX(0);     opacity: 1; }
                    }
                    @keyframes slideInRight {
                        from { transform: translateX(130%); opacity: 0; }
                        to   { transform: translateX(0);    opacity: 1; }
                    }
                `}
            />

            <Box
                sx={{
                    textAlign: "center",
                    background: "#71bae5",
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Top-left cloud — slides in from left */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 100,
                        left: 0,
                        zIndex: 0,
                        animation: "slideInLeft 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both",
                    }}
                >
                    <img src="/assets/Vector-one.png" alt="cloud-top-left" />
                </Box>

                {/* Top-right cloud — slides in from right */}
                <Box
                    sx={{
                        position: "absolute",
                        right: 0,
                        top: 200,
                        zIndex: 0,
                        animation: "slideInRight 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both",
                    }}
                >
                    <img src="/assets/Vector-two.png" alt="cloud-top-right" />
                </Box>

                {/* Form content */}
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{ flex: 1 }}
                >
                    <Grid item md={4} sm={6} xs={10}>
                        <img
                            src="/assets/auth-brand-logo.png"
                            alt="brand-logo"
                            width="100px"
                        />
                        <Typography
                            sx={{
                                fontSize: "25px",
                                margin: "10px 0 18px 0",
                                color: "white",
                                fontWeight: "bold",
                            }}
                        >
                            KIOSK Login
                        </Typography>

                        {/* Username field */}
                        <Box sx={{ textAlign: "left" }}>
                            <label>Username</label>
                            <TextField
                                sx={{
                                    zIndex: 10,
                                    position: "relative",
                                    borderRadius: "8px",
                                    backgroundColor: "#fff",
                                }}
                                autoFocus={false}
                                autoComplete="off"
                                type="text"
                                fullWidth
                                name="username"
                                value={cashierData.username}
                                id="cashier"
                                placeholder="Tap here to type"
                                variant="outlined"
                                onChange={handleInputChange}
                                error={!!errors.username}
                            />
                            <p
                                style={{
                                    fontSize: "12px",
                                    marginTop: "-1px",
                                    color: "red",
                                }}
                            >
                                {errors.username}
                            </p>
                        </Box>

                        {/* Password field */}
                        <Box sx={{ textAlign: "left", marginTop: "21px" }}>
                            <label>Password</label>
                            <TextField
                                sx={{
                                    zIndex: 10,
                                    position: "relative",
                                    borderRadius: "8px",
                                    backgroundColor: "#fff",
                                }}
                                autoFocus={false}
                                autoComplete="off"
                                fullWidth
                                name="password"
                                placeholder="Tap here to type"
                                variant="outlined"
                                type={showPassword ? "text" : "password"}
                                value={cashierData.password}
                                onChange={handleInputChange}
                                error={!!errors.password}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handlePasswordVisibility}
                                                edge="end"
                                            >
                                                {showPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <p
                                style={{
                                    fontSize: "12px",
                                    marginTop: "-1px",
                                    color: "red",
                                }}
                            >
                                {errors.password}
                            </p>
                        </Box>

                        {/* Submit button */}
                        <Grid
                            container
                            justifyContent="center"
                            style={{ marginTop: "21px" }}
                        >
                            <Grid item sm={12} md={12}>
                                <PrimaryButton
                                    title="Proceed"
                                    onClick={handleLogin}
                                    disabled={isLoading}
                                    spinner={isLoading}
                                    disabledTitle="Logging In"
                                    style={{
                                        borderRadius: "30px",
                                        fontSize: "18px",
                                        width: "100%",
                                        marginTop: "10px",
                                        marginBottom: "20px",
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Bottom-left cloud — slides in from left */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 60,
                        left: 0,
                        zIndex: 0,
                        animation: "slideInLeft 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both",
                    }}
                >
                    <img src="/assets/Vector-one.png" alt="cloud-bottom-left" />
                </Box>

                {/* Bottom-right cloud — slides in from right */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 20,
                        right: 0,
                        zIndex: 0,
                        animation: "slideInRight 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.7s both",
                    }}
                >
                    <img src="/assets/Vector-two.png" alt="cloud-bottom-right" />
                </Box>
            </Box>
        </>
    );
};

export default CashierLogin;